import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, faqs } from '@/mocks/movies';
import { getWebPageSchema, getBreadcrumbSchema, getFAQSchema, getOrganizationSchema } from '@/utils/schema';
export default function About() {
  const pageTitle = '关于春色影视 - 品牌介绍与联系方式';
  const pageDesc = '了解春色影视品牌故事、用户口碑评价与联系方式。春色影视致力于提供高清免费电影电视剧在线观看服务，每日更新最新影视资源，流畅不卡顿。';

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const webPageSchema = getWebPageSchema(
    pageTitle,
    pageDesc,
    'https://chunse.yingshi.com/about',
    getBreadcrumbSchema([
      { name: '首页', url: 'https://chunse.yingshi.com' },
      { name: '关于我们', url: 'https://chunse.yingshi.com/about' },
    ]).itemListElement
  );

  const [activeTab, setActiveTab] = useState<'intro' | 'reviews' | 'contact'>('intro');

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-2-line' },
    { key: 'reviews' as const, label: '用户口碑', icon: 'ri-chat-smile-2-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-mail-send-line' },
  ];

  return (
    <>
      <SEO
        title="关于我们"
        description={pageDesc}
        keywords="春色影视,关于春色影视,春色影视联系方式,春色影视口碑,免费影视平台"
        canonicalUrl="https://chunse.yingshi.com/about"
        schema={{ ...getOrganizationSchema(), ...webPageSchema }}
      />

      <Navbar />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={breadcrumbItems} />

        <h1 className="text-2xl md:text-3xl font-heading text-foreground-100 mt-4 mb-8">
          关于春色影视
        </h1>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-8 max-w-md">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex-1 justify-center cursor-pointer ${
                activeTab === tab.key
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-400 hover:text-foreground-200'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={tab.icon}></i>
              </span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Brand Introduction */}
        {activeTab === 'intro' && (
          <section className="space-y-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-xl font-heading text-foreground-100 mb-4">
                  <a href="#brand-story">品牌故事</a>
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                  春色影视成立于2020年，是一个专注于为广大影迷提供高清影视免费在线观看服务的平台。我们深知影迷们对优质观影体验的追求，因此始终致力于打造一个资源丰富、播放流畅、界面清爽的免费影视平台。
                </p>
                <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                  经过多年的发展与积累，春色影视已经建立起涵盖电影、电视剧、综艺、动漫、纪录片等全类型的影视资源库。无论是热播大剧、经典老片，还是最新院线大片，您都可以在春色影视找到并免费观看。
                </p>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  我们的团队由一群热爱影视的年轻人组成，每日更新最新影视资源，确保用户能够第一时间观看到想看的内容。春色影视始终坚持<strong className="text-primary-500 font-semibold">"用户体验至上"</strong>的理念，不断优化平台性能，让每一位用户都能获得流畅、高清、舒适的观影体验。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20multiple%20monitors%20displaying%20film%20and%20video%20content%2C%20warm%20ambient%20lighting%2C%20creative%20team%20collaboration%20atmosphere%2C%20cinematic%20posters%20on%20wall%2C%20professional%20yet%20cozy%20environment&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="春色影视团队工作环境"
                  title="春色影视 - 专业的影视内容团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: '10万+', label: '影视资源', icon: 'ri-database-2-line' },
                { value: '500万+', label: '活跃用户', icon: 'ri-user-star-line' },
                { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
                { value: '24h', label: '更新速度', icon: 'ri-timer-flash-line' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex flex-col items-center gap-2 p-5 rounded-lg bg-background-100 border border-background-200/50"
                >
                  <span className="w-8 h-8 flex items-center justify-center text-accent-500">
                    <i className={`${stat.icon} text-2xl`}></i>
                  </span>
                  <span className="text-xl md:text-2xl font-heading text-foreground-100">{stat.value}</span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{stat.label}</span>
                </div>
              ))}
            </div>

            <div>
              <h2 className="text-xl font-heading text-foreground-100 mb-4">
                <a href="#core-values">核心优势</a>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    title: '完全免费',
                    desc: '无需注册，无需付费，所有影视资源完全免费在线观看。',
                    icon: 'ri-money-cny-circle-line',
                  },
                  {
                    title: '高清流畅',
                    desc: '多条播放线路，高清画质，流畅不卡顿的观影体验。',
                    icon: 'ri-4k-line',
                  },
                  {
                    title: '每日更新',
                    desc: '最新电影、热播剧集、热门综艺第一时间同步更新。',
                    icon: 'ri-refresh-line',
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-5 rounded-lg bg-background-100 border border-background-200/50"
                  >
                    <span className="w-10 h-10 flex items-center justify-center text-primary-500 mb-3">
                      <i className={`${item.icon} text-2xl`}></i>
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-200 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* User Reviews */}
        {activeTab === 'reviews' && (
          <section>
            <h2 className="text-xl font-heading text-foreground-100 mb-6">
              <a href="#user-reviews">用户口碑</a>
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              以下评价来自春色影视的真实用户，感谢每一位影迷的支持与信任！
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t, index) => (
                <div
                  key={index}
                  className="p-5 rounded-lg bg-background-100 border border-background-200/50"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span
                        key={i}
                        className="w-4 h-4 flex items-center justify-center"
                      >
                        <i
                          className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-600'}`}
                        ></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-3">
                    "{t.comment}"
                  </p>
                  <p className="text-xs text-foreground-600 font-medium">{t.name}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Contact */}
        {activeTab === 'contact' && (
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-xl font-heading text-foreground-100 mb-4">
                <a href="#contact-us">联系我们</a>
              </h2>
              <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们会在24小时内回复您的消息。
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@chunseyingshi.com' },
                  { icon: 'ri-wechat-line', label: '微信公众号', value: '春色影视' },
                  { icon: 'ri-weibo-line', label: '官方微博', value: '@春色影视官微' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/50">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 shrink-0">
                      <i className={item.icon}></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{item.label}</p>
                      <p className="text-sm text-foreground-200 font-medium">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="text-lg font-heading text-foreground-200 mb-4">
                <a href="#faq-section">常见问题</a>
              </h3>
              <div className="space-y-2">
                {faqs.slice(0, 4).map((faq, index) => (
                  <details
                    key={index}
                    className="group rounded-lg bg-background-100 border border-background-200/50 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer text-sm font-medium text-foreground-300 hover:text-foreground-200 transition-colors duration-200 list-none">
                      {faq.q}
                      <span className="w-4 h-4 flex items-center justify-center shrink-0 ml-2">
                        <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                      </span>
                    </summary>
                    <div className="px-4 pb-3 text-sm text-foreground-500 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>

              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(getFAQSchema(faqs.slice(0, 4))) }}
              />
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200/50 p-6">
              <h3 className="text-lg font-heading text-foreground-200 mb-4">
                <a href="#contact-form">留言反馈</a>
              </h3>

              <form
                id="contact-form"
                data-readdy-form
                className="space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const honeypot = formData.get('phone_alt');
                  if (honeypot && String(honeypot).trim() !== '') {
                    alert('留言已提交，感谢您的反馈！');
                    form.reset();
                    return;
                  }
                  formData.delete('phone_alt');
                  try {
                    const params = new URLSearchParams();
                    formData.forEach((value, key) => {
                      params.append(key, String(value));
                    });
                    const response = await fetch('https://readdy.ai/api/form/d9njphu8mbnljin5a21g', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: params.toString(),
                    });
                    const responseText = await response.text();
                    let serverMsg = '留言已提交，感谢您的反馈！';
                    try {
                      const parsed = JSON.parse(responseText);
                      if (parsed?.meta?.message) serverMsg = parsed.meta.message;
                    } catch {
                      // use default message
                    }
                    if (response.ok) {
                      alert(serverMsg);
                      form.reset();
                    } else {
                      alert(serverMsg || '提交失败，请稍后再试。');
                    }
                  } catch {
                    // network error, show generic message
                    alert('网络错误，请稍后再试。');
                  }
                }}
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-400 mb-1.5">
                    您的称呼 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/50 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors duration-200"
                    placeholder="请输入您的称呼"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-400 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/50 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors duration-200"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm text-foreground-400 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/50 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors duration-200"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-400 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/50 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors duration-200 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-send-plane-line"></i>
                  </span>
                  提交留言
                </button>
              </form>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}